import type { ReactNode } from 'react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useLocation } from '@tanstack/react-router';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import {
  askForClassicDashboard,
  asksForClassicDashboard,
  dashboardPath,
  homePath,
  legacyDashboardPath,
  withLocation,
} from './urls';

/**
 * The two URLs the classic dashboard answers to while
 * `classic-dashboard-migration` rolls out, and how each hands off to the
 * other.
 *
 * They do not mean quite the same thing. The migrated URL means this page and
 * nothing else. The `journeys` URL means the workspace landing page once the
 * new home is live, and asks for the classic dashboard only by name — so a
 * handoff in either direction has to say which page the reader was heading
 * for, rather than only which app is serving it.
 */

/** Where the current location resolves to, or null until the workspace id lands. */
function useHandoffTarget(buildPath: (workspaceId: string) => string, searchStr?: string): string | null {
  const location = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(location.pathname);

  if (workspaceId == null) {
    return null;
  }
  return withLocation(buildPath(String(workspaceId)), searchStr ?? location.searchStr, location.hash);
}

type DashboardRouteProps = {
  children: ReactNode;
};

/**
 * The migrated URL. Flag off, it hands back to the `journeys` URL the
 * embedded app serves, naming the classic dashboard so a rollback lands on
 * the page the reader held a link to rather than on home.
 */
export function DashboardRoute({ children }: DashboardRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ClassicDashboardMigration);
  const { searchStr } = useLocation();
  const legacyHref = useHandoffTarget(legacyDashboardPath, askForClassicDashboard(searchStr));
  // The page's reads go through `requireWorkspaceId`, which throws while the
  // workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {children}
    </MigrationRouteView>
  );
}

/**
 * The `journeys` URL, which forwards once the flag is on — to home unless the
 * link asks for the classic dashboard. The embedded app resolves that same
 * fork itself while the flag is off, reading the same account flag.
 */
export function JourneysDashboardHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ClassicDashboardMigration);
  const isHomeLive = useFeatureFlag(AccountFeatureFlag.HomeUx);
  const { searchStr } = useLocation();
  const classicHref = useHandoffTarget(dashboardPath);
  // Home is a different page, not the same one at another URL, so it is
  // reached plainly — as the page that hands off to it does.
  const homeHref = useHandoffTarget(homePath, '');

  const wantsClassic = asksForClassicDashboard(searchStr);
  const migratedHref = isHomeLive && !wantsClassic ? homeHref : classicHref;

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}
